import React, { useState } from "react";
import { Search, Download, Package } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { lookupOrders, API } from "@/lib/api";
import { toast } from "sonner";

const OrderLookup = () => {
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await lookupOrders(email.trim());
      setOrders(res);
      if (res.length === 0) toast.info("No orders found for that email yet.");
    } catch {
      toast.error("Unable to look up orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
    catch { return ""; }
  };

  return (
    <main className="bg-cream min-h-screen pt-32 pb-28" data-testid="order-lookup-page">
      <section className="bg-emerald text-cream py-20 md:py-28">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow text-gold mb-6">Order Lookup</p>
            <h1 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight max-w-2xl">
              Find your orders & <span className="italic text-gold">re-download</span> your ebook.
            </h1>
            <p className="font-editorial italic text-2xl text-cream/80 mt-6 max-w-xl">
              Enter the email you used at checkout to access your purchases anytime.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto px-6 md:px-10 py-16">
        <Reveal>
          <form onSubmit={search} className="flex flex-col sm:flex-row gap-4 mb-14" data-testid="lookup-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="lookup-email"
              className="flex-1 bg-transparent border-b border-line focus:border-emerald outline-none py-3 font-body text-ink placeholder:text-ink/30 transition-colors"
            />
            <button type="submit" disabled={loading} className="btn-emerald disabled:opacity-60" data-testid="lookup-submit">
              {loading ? "Searching…" : <><Search className="w-4 h-4" /> Find my orders</>}
            </button>
          </form>
        </Reveal>

        {orders && orders.length === 0 && (
          <Reveal>
            <div className="text-center py-16 border border-line bg-surface" data-testid="lookup-empty">
              <Package className="w-10 h-10 text-emerald mx-auto mb-4" strokeWidth={1.2} />
              <p className="font-editorial italic text-2xl text-emerald">No orders found for that email.</p>
              <p className="font-body text-ink/60 text-sm mt-2">Orders appear here once payment is confirmed.</p>
            </div>
          </Reveal>
        )}

        {orders && orders.length > 0 && (
          <div className="space-y-6" data-testid="lookup-results">
            {orders.map((o) => (
              <Reveal key={o.session_id}>
                <div className="border border-line bg-surface p-8" data-testid={`order-${o.session_id}`}>
                  <div className="flex flex-wrap justify-between items-center gap-4 border-b border-line pb-5 mb-5">
                    <div>
                      <p className="eyebrow text-emerald mb-1">Order · {fmtDate(o.created_at)}</p>
                      <p className="font-body text-xs text-ink/40">Ref: {o.session_id.slice(-10)}</p>
                    </div>
                    {o.amount != null && <p className="font-display text-2xl text-emerald">${o.amount.toFixed(2)}</p>}
                  </div>
                  <ul className="space-y-2 mb-6">
                    {o.items.map((it, i) => (
                      <li key={i} className="flex justify-between font-body text-sm text-ink/80">
                        <span>{it.name} × {it.quantity}</span>
                        <span>${(it.price * it.quantity).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                  {o.has_ebook && (
                    <div className="flex flex-wrap gap-3 pt-2">
                      <a href={`${API}/download/${o.session_id}?format=pdf`} className="btn-emerald !py-2.5 !px-5 text-sm" data-testid={`redl-pdf-${o.session_id}`}>
                        <Download className="w-4 h-4" /> PDF
                      </a>
                      <a href={`${API}/download/${o.session_id}?format=epub`} className="btn-outline !py-2.5 !px-5 text-sm" data-testid={`redl-epub-${o.session_id}`}>
                        <Download className="w-4 h-4" /> EPUB
                      </a>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default OrderLookup;
